const GLOBAL_RELOAD_KEY = "fysaro:vite-preload-reload";
const RELOAD_COOLDOWN_MS = 10_000;

export function isStaleChunkMessage(message: string): boolean {
  const normalized = message.toLowerCase();

  return (
    normalized.includes("failed to fetch dynamically imported module") ||
    normalized.includes("importing a module script failed") ||
    normalized.includes("error loading dynamically imported module") ||
    normalized.includes("failed to load module script") ||
    normalized.includes("mime type") ||
    normalized.includes("loading chunk") ||
    normalized.includes("chunkloaderror")
  );
}

export function isStaleChunkScriptSrc(src: string | undefined): boolean {
  return isStaleAssetHref(src);
}

export function isStaleAssetHref(src: string | undefined): boolean {
  if (!src) return false;
  return (
    src.includes("/assets/") &&
    (src.endsWith(".js") || src.endsWith(".css"))
  );
}

/**
 * Reload once after a deployment replaces Vite's hashed assets while a tab
 * still references the previous bundle.
 */
export function reloadOnceForStaleChunks(reason: string): boolean {
  if (typeof window === "undefined") return false;

  const lastReloadAt = Number(sessionStorage.getItem(GLOBAL_RELOAD_KEY) ?? 0);
  if (Date.now() - lastReloadAt < RELOAD_COOLDOWN_MS) {
    console.warn("[Fysaro] Stale chunk detected but reload cooldown is active.", {
      reason,
    });
    return false;
  }

  sessionStorage.setItem(GLOBAL_RELOAD_KEY, String(Date.now()));

  const url = new URL(window.location.href);
  url.searchParams.set("_chunk_reload", String(Date.now()));
  window.location.replace(url.toString());
  return true;
}

export function registerStaleChunkRecovery(): void {
  if (typeof window === "undefined") return;

  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    reloadOnceForStaleChunks("vite:preloadError");
  });

  window.addEventListener(
    "error",
    (event) => {
      const target = event.target;

      if (target instanceof HTMLScriptElement && isStaleAssetHref(target.src)) {
        event.preventDefault();
        reloadOnceForStaleChunks(`script:${target.src}`);
        return;
      }

      if (
        target instanceof HTMLLinkElement &&
        target.rel === "stylesheet" &&
        isStaleAssetHref(target.href)
      ) {
        event.preventDefault();
        reloadOnceForStaleChunks(`stylesheet:${target.href}`);
      }
    },
    true,
  );

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === "string"
          ? reason
          : String(reason ?? "");

    if (!isStaleChunkMessage(message)) return;

    event.preventDefault();
    reloadOnceForStaleChunks(`unhandledrejection:${message}`);
  });
}
